// Punctul de intrare: meniul lateral al platformei, taburile de screener,
// rutarea și starea globală.

import { AlertBanners, watchUrl } from './ui/alert-banners.js';
import { el, clear, bus } from './core/util.js';
import { icon } from './ui/icons.js';
import { settings, market } from './core/state.js';
import { applyTheme } from './core/theme.js';
import { client } from './core/exchange-client.js';
import { toast } from './ui/components.js';
import { Sidebar } from './ui/sidebar.js';
import { BtcWidget } from './ui/btc-widget.js';
import { GlobalSearch } from './ui/global-search.js';
import { DashboardSection } from './sections/dashboard.js';
import { GridSection } from './sections/grid.js';
import { CoinsSection } from './sections/coins.js';
import { VolumeRadarSection } from './sections/volume-radar.js';
import { DensitySection } from './sections/density.js';
import { ListingsSection } from './sections/listings.js';
import { SettingsSection } from './sections/settings.js';
import { ChatSection } from './sections/chat.js';
import { IdeasSection } from './sections/ideas.js';
import { ReportsSection } from './sections/reports.js';
import { TrainingSection } from './sections/training.js';
import { openSupport } from './ui/support.js';
import { ProfileSection } from './sections/profile.js';
import { LeaderboardSection } from './sections/leaderboard.js';
import { MembersSection } from './sections/members.js';
import { MessagesSection } from './sections/messages.js';
import { AdminSection } from './sections/admin.js';
import { FormationsSection } from './sections/formations.js';
import { RulesSection } from './sections/rules.js';
import { AboutSection } from './sections/about.js';
import { StrategiesSection } from './sections/strategies.js';
import { FeedSection } from './sections/feed.js';
import { AccountSection } from './sections/account.js';
import { openAuth, openReset } from './ui/auth-modal.js';
import { session } from './core/session.js';
import { ecranIntrare } from './ui/welcome.js';
import { esteMobil, porneste as pornesteDispozitiv } from './core/device.js';
import { MobileShell } from './ui/mobile-shell.js';
import { MobileScreenerSection } from './sections/mobile-screener.js';

/** Taburile de screener — nume și iconițe identice cu ScalpX. */
const SCREENER_TABS = [
  { path: '/screener/coins/', label: 'Список наблюдения', icon: 'eye', section: CoinsSection },
  { path: '/screener/', label: 'Сетка', icon: 'grid', section: GridSection },
  { path: '/screener/volume-radar/', label: 'Радар объёмов', icon: 'radar', section: VolumeRadarSection },
  { path: '/screener/density/', label: 'Терминал плотностей', icon: 'rows', section: DensitySection },
  { path: '/screener/formations/', label: 'Формации', icon: 'layerGroup', section: FormationsSection },
  { path: '/screener/listings/', label: 'Календарь листингов', icon: 'calendar', section: ListingsSection },
  { path: '/screener/settings/', label: 'Настройки', icon: 'userGear', section: SettingsSection },
];

const ROUTES = [
  { path: '/', label: 'Дашборд', section: DashboardSection, shell: 'platform' },
  { path: '/feed/', label: 'Лента', section: FeedSection, shell: 'platform' },
  { path: '/chat/', label: 'Общий чат', section: ChatSection, shell: 'platform' },
  { path: '/publications/', label: 'Торговые идеи', section: IdeasSection, shell: 'platform' },
  { path: '/reports/', label: 'Торговые отчёты', section: ReportsSection, shell: 'platform' },
  { path: '/preplay/', label: 'Тренажёр', section: TrainingSection, shell: 'platform' },
  { path: '/profile/', label: 'Профиль', section: ProfileSection, shell: 'platform' },
  { path: '/members/', label: 'Участники', section: MembersSection, shell: 'platform' },
  { path: '/messages/', label: 'Сообщения', section: MessagesSection, shell: 'platform' },
  { path: '/leaderboard/', label: 'Лидеры рейтинга', section: LeaderboardSection, shell: 'platform' },
  { path: '/admin/', label: 'Участники', section: AdminSection, shell: 'platform' },
  { path: '/account/', label: 'Настройки аккаунта', section: AccountSection, shell: 'platform' },
  { path: '/strategies/', label: 'Стратегии', section: StrategiesSection, shell: 'platform' },
  { path: '/about/', label: 'О платформе', section: AboutSection, shell: 'platform' },
  { path: '/rules/', label: 'Правила', section: RulesSection, shell: 'platform' },
  ...SCREENER_TABS.map((t) => ({ ...t, shell: 'screener' })),
];

const BUILD = '2.0.0';

class App {
  constructor() {
    this.current = null;
    this.exchange = settings.get('sx_exchange', 'binance');
    // Piaţa se ţine minte la fel ca bursa. Înainte era scrisă fix aici, deci după
    // fiecare reîncărcare omul care lucrează pe spot ateriza din nou pe futures.
    this.market = settings.get('sx_market', 'futures');
  }

  async boot() {
    // Se hotaraste o data, inainte de orice desen: telefonul primeste alta interfata,
    // nu aceeasi intinsa altfel. Vezi `core/device.js`.
    this.mobil = esteMobil();
    pornesteDispozitiv();

    // Cine ești se află în paralel cu setările: sunt independente, iar una din ele
    // n-are de ce s-o aștepte pe cealaltă la pornire.
    await Promise.all([settings.load(), session.load()]);
    // Recitim **amândouă** după ce setările au ajuns de la server. Constructorul rulează
    // înaintea încărcării, deci acolo se citesc doar valorile implicite — bursa era deja
    // recitită aici, piaţa nu, şi de asta ea revenea la „фьючерсы" după fiecare
    // reîncărcare, deşi se salva corect.
    this.exchange = settings.get('sx_exchange', 'binance');
    this.market = settings.get('sx_market', 'futures');

    this.#renderShell();
    // Fluxul de burse porneste, dar **nu-l asteptam**. Pana acum aici era un `await`,
    // iar el intarzia desenarea oricarei pagini pana venea tabelul cu sutele de monede
    // — inclusiv profilul si lenta, care n-au nevoie de el deloc. De acolo venea
    // senzatia ca deschiderea profilului „trece prin screener".
    //
    // Sectiunile de screener nu pierd nimic: toate asculta `market:aggregates` si se
    // redeseneaza cand datele ajung, exact cum fac la fiecare poll de cinci secunde.
    const piata = market.start(this.exchange, this.market);
    piata.catch((err) => console.warn('[piata] pornirea a esuat', err));

    await this.#handleAuthLink();
    window.addEventListener('popstate', () => this.#route());
    // Intrarea şi ieşirea rescriu tot ecranul: la ieşire nu mai are ce rămâne pe el,
    // la intrare trebuie construită aplicaţia care până atunci nu exista.
    bus.on('session:change', () => {
      this.shellEl.classList.toggle('inchis', !session.authenticated);
      this.#route();
    });
    this.#route();
    this.#startNotifications();

    // Peste toate filele, nu în interiorul vreuneia: fereastra BTC trebuie să rămână
    // deschisă și când treci de la grilă la densități.
    this.btcWidget = new BtcWidget(this);
    this.globalSearch = new GlobalSearch(this);
    bus.on('symbol:open', ({ symbol }) => this.openSymbol(symbol));
    bus.on('tiger:error', (mesaj) => toast(mesaj, 'error'));
    bus.on('profile:open', ({ username }) => this.navigate(`/profile/${username}/`));
    // «Поделиться» din screener: ducem datele graficului la secțiunea de idei, care
    // deschide formularul precompletat imediat ce se montează.
    bus.on('idea:compose', (payload) => {
      this.pendingIdea = payload;
      this.navigate('/publications/');
    });
    // Chatul poate să nu fie deschis: îl deschidem și livrăm mesajul după montare.
    bus.on('chat:send', (payload) => {
      if (this.current instanceof ChatSection) return;   // secțiunea îl tratează singură
      this.pendingChat = payload;
      this.navigate('/chat/');
    });
  }

  /**
   * Linkurile din e-mail: `/confirm/<jeton>` și `/reset/<jeton>`.
   *
   * Se tratează înainte de rutare, iar adresa se curăță imediat după — un jeton
   * rămas în bara de adrese ajunge în istoric și în orice captură de ecran.
   */
  async #handleAuthLink() {
    const m = location.pathname.match(/^\/(confirm|reset)\/([\w-]+)\/?$/);
    if (!m) return;
    const [, fel, token] = m;
    history.replaceState({}, '', '/');

    if (fel === 'confirm') {
      try {
        const user = await session.confirm(token);
        // Confirmarea deschide şi sesiunea, deci mesajul o spune: altfel omul nu ştie
        // că e deja înăuntru şi caută unde să se autentifice.
        toast(user ? `Почта подтверждена. Добро пожаловать, ${user.username}!`
                   : 'Почта подтверждена', 'success');
      } catch (err) {
        toast(err.message || 'Ссылка недействительна', 'error');
      }
      return;
    }
    openReset(token);
  }

  /**
   * Deschide un instrument ales din sugestii, cu tot cu bursa și piața lui.
   *
   * Sugestiile arată aceeași monedă pe mai multe burse, deci alegerea poartă și
   * locul: dacă utilizatorul a ales BTC pe Bybit spot, comutăm acolo înainte să
   * deschidem graficul — altfel am arăta alt instrument decât cel apăsat.
   */
  /**
   * Список наблюдения cu moneda dată, pe bursa ei — din bannerul de alertă sau dintr-o
   * adresă `/screener/coins/?symbol=…&exchange=…&market=…` (deschisă cu rotița într-un tab nou).
   */
  async openWatch({ symbol, exchange, market }) {
    settings.set('sx_coins_symbol', symbol);
    if (exchange) settings.set('sx_coins_chart_ex', exchange);
    if (market && market !== this.market) await this.setMarket(market);
    this.navigate(watchUrl({ symbol, exchange, market }), { fortat: true });
  }

  async openInstrument({ symbol, exchange, market }) {
    // Simbolul se scrie înainte de comutări: ambele metode cheamă `#ensureSymbolExists`,
    // iar fără el ar sări pe cea mai lichidă monedă în loc de cea aleasă.
    settings.set('sx_coins_symbol', symbol);
    // Amândouă ies imediat dacă nu e nimic de schimbat, deci le putem chema pe rând.
    if (exchange) await this.setExchange(exchange);
    if (market) await this.setMarket(market);

    await this.openSymbol(symbol);
  }

  /**
   * Deschide o monedă pe graficul mare, de oriunde din screener.
   *
   * Dacă secțiunea curentă are deja un panou (lista de monede, densitățile), moneda se
   * schimbă în el. Altfel trecem la lista de monede — acolo graficul e cel mare.
   */
  async openSymbol(symbol) {
    const panel = this.current?.panel;
    if (panel?.setSymbol) {
      await panel.setSymbol(symbol);
      return;
    }
    // Secțiunea își ia moneda din setare la montare. Scriem acolo în loc să ne luptăm
    // cu ordinea: altfel ea și-ar alege singură moneda peste a noastră.
    settings.set('sx_coins_symbol', symbol);
    this.pendingSymbol = symbol;
    this.navigate('/screener/coins/');
  }

  // ------------------------------------------------------------------ shell

  #renderShell() {
    if (this.mobil) { this.#renderShellMobil(); return; }
    this.shellEl = el('div', { class: 'shell' });
    this.sidebar = new Sidebar(this).mount(this.shellEl);

    this.mainEl = el('div', { class: 'main' });
    // Ascunsa din start: pana la prima rutare ea era vizibila pe orice adresa, deci
    // la fiecare reincarcare a profilului aparea o clipa bara screenerului.
    this.tabsBarEl = el('nav', { class: 'screener-tabs', style: 'display:none' });
    this.viewEl = el('main', { class: 'view' });
    this.mainEl.append(this.tabsBarEl, this.viewEl);
    this.shellEl.append(this.mainEl);
    document.body.append(this.shellEl);

    this.tabLinks = new Map();
    const tabs = el('div', { class: 'screener-tabs-list' }, [
      // Bara laterală e ascunsă în screener; acesta e drumul înapoi spre dashboard.
      el('button', {
        class: 'tabs-home', title: 'На дашборд',
        onclick: () => this.navigate('/'),
      }, [
        el('span', { class: 'tabs-home-s', text: 'S' }),
        el('span', { class: 'tabs-home-x', text: 'X' }),
      ]),
    ]);
    for (const tab of SCREENER_TABS) {
      const link = el('a', {
        class: 'topnav-tab',
        href: tab.path,
        onclick: (event) => { event.preventDefault(); this.navigate(tab.path); },
      }, [icon(tab.icon, 14), el('span', { text: tab.label })]);
      this.tabLinks.set(tab.path, link);
      tabs.append(link);
    }

    this.statusEl = el('span', { class: 'topnav-status', title: 'Соединение с биржей' });
    this.bellCountEl = el('span', { class: 'topnav-badge', text: '0' });
    this.bellEl = el('button', {
      class: 'topnav-bell', title: 'История уведомлений',
      onclick: () => this.#openNotifications(),
    }, [icon('bell', 14), this.bellCountEl]);

    this.tabsBarEl.append(
      tabs,
      el('div', { class: 'screener-tabs-right' }, [
        el('span', { class: 'build-badge' }, [
          el('span', { class: 'build-dot' }),
          el('span', { text: 'ScalpScreener' }),
          el('span', { class: 'build-ver', text: BUILD }),
        ]),
        this.statusEl,
        el('button', {
          class: 'topnav-bug', title: 'Нашли баг? Расскажите нам',
          onclick: () => openSupport(),
        }, [icon('bug', 13), el('span', { text: 'Нашли баг?' })]),
        this.bellEl,
      ]),
    );

    // Indicatorul arată starea perechii deschise, nu ultimul eveniment de pe orice
    // conexiune: altfel spotul „viu" ar masca futures-ul care merge pe interogare.
    const mine = (e) => e.exchange === this.exchange && e.market === this.market;
    bus.on('ws:open', (e) => { if (mine(e)) this.#refreshStatus(); });
    bus.on('ws:close', (e) => { if (mine(e)) this.#setStatus('offline'); });
    bus.on('feed:degraded', (e) => { if (mine(e)) this.#setStatus('polling'); });
    bus.on('feed:live', (e) => { if (mine(e)) this.#setStatus('live'); });
    // Pauza impusă de bursă începe și se termină fără să anunțe nimeni, deci
    // indicatorul se recitește periodic, nu doar la evenimente.
    setInterval(() => this.#refreshStatus(), 5000);
    this.#setStatus('offline');
  }

  /** Recalculează indicatorul din starea reală a perechii active. */
  #refreshStatus() {
    const connected = client.status().some(
      (c) => c.key === `${this.exchange}:${this.market}` && c.ready,
    );
    if (!connected) { this.#setStatus('offline'); return; }
    // Ordinea contează: dacă bursa ne-a pus în pauză, nici interogarea nu merge,
    // iar „ОПРОС" ar fi o minciună la fel de mare ca „LIVE".
    if (client.throttled(this.exchange, this.market)) { this.#setStatus('throttled'); return; }
    this.#setStatus(client.degraded(this.exchange, this.market) ? 'polling' : 'live');
  }

  #setStatus(state) {
    // Pe telefon nu exista bara cu indicatorul de conexiune: acolo ecranul e al
    // graficului, nu al starii lui.
    if (!this.statusEl) return;
    const label = {
      live: 'LIVE', polling: 'ОПРОС', throttled: 'ЛИМИТ', offline: 'OFFLINE',
    }[state];
    const title = {
      live: 'Данные идут потоком с биржи',
      polling: 'Биржа не отдаёт поток в этой сети — данные обновляются опросом каждую секунду',
      throttled: 'Биржа временно ограничила частоту запросов — обновления возобновятся автоматически',
      offline: 'Нет соединения с биржей',
    }[state];
    this.statusEl.className = `topnav-status ${state}`;
    this.statusEl.textContent = label;
    this.statusEl.title = title;
  }

  // ---------------------------------------------------------------- rutare

  navigate(path, { fortat = false } = {}) {
    if (location.pathname + location.search === path && !fortat) return;
    history.pushState({}, '', path);
    this.#route();
  }

  /** Shell-ul de telefon: bara sus, meniu jos, restul pe tot ecranul. */
  #renderShellMobil() {
    this.mobileShell = new MobileShell(this).mount();
    this.shellEl = this.mobileShell.root;
    this.viewEl = this.mobileShell.viewEl;
    // Bara de file a screener-ului nu exista pe telefon; codul de rutare o cauta
    // oricum, deci ii dam un element care nu se vede nicaieri.
    this.tabsBarEl = el('nav', { style: 'display:none' });

    const mine = (e) => e.exchange === this.exchange && e.market === this.market;
    bus.on('ws:open', (e) => { if (mine(e)) this.#refreshStatus(); });
    bus.on('feed:degraded', (e) => { if (mine(e)) this.#setStatus('polling'); });
    bus.on('feed:live', (e) => { if (mine(e)) this.#setStatus('live'); });
  }

  #route() {
    const path = location.pathname;

    // Poarta: fără cont nu există nicio secţiune, doar ecranul de intrare. Verificarea
    // stă aici, în router, nu în fiecare secţiune — o secţiune uitată ar fi o uşă
    // deschisă, iar uşile deschise se descoperă abia după ce a intrat cineva.
    //
    // Excepţia sunt legăturile din e-mail: confirmarea şi resetarea trebuie să meargă
    // tocmai când **nu** eşti autentificat. Ele se tratează înainte de rutare şi îşi
    // curăţă singure adresa, deci aici ajunge deja „/".
    if (!session.authenticated) {
      this.#poarta();
      return;
    }
    if (this.poartaEl) { this.poartaEl.remove(); this.poartaEl = null; }
    // Profilul are adresă dinamică (`/profile/<nick>/`), deci potrivirea exactă nu-l prinde.
    let route = ROUTES.find((r) => r.path === path)
      || (path.startsWith('/profile') ? ROUTES.find((r) => r.path === '/profile/') : null)
      || (path.startsWith('/screener') ? ROUTES.find((r) => r.path === '/screener/') : ROUTES[0]);

    // Pe telefon, toate filele screener-ului duc la acelasi ecran: lista de monede si
    // graficul. Grila, radarul si densitatile sunt tabele largi — pe sase toli ele nu
    // se micsoreaza, se fac ilizibile.
    if (this.mobil && route.shell === 'screener') {
      route = { path, label: 'Скринер', section: MobileScreenerSection, shell: 'platform' };
    }

    // Taburile de screener apar doar în interiorul screener-ului.
    const inScreener = route.shell === 'screener';
    if (!this.mobil) this.tabsBarEl.style.display = inScreener ? '' : 'none';
    this.shellEl.classList.toggle('platform', !inScreener);
    for (const [p, link] of (this.tabLinks || [])) link.classList.toggle('active', p === route.path);
    this.sidebar?.setActive(path);
    this.mobileShell?.aplicaRuta(path, route.label);

    // `/screener/coins/?symbol=…` — de exemplu un banner deschis cu rotița într-un tab nou.
    if (path === '/screener/coins/') {
      const q = new URLSearchParams(location.search);
      const sym = q.get('symbol');
      if (sym) {
        settings.set('sx_coins_symbol', sym.toUpperCase());
        if (q.get('exchange')) settings.set('sx_coins_chart_ex', q.get('exchange'));
        const mk = q.get('market');
        if (mk && mk !== this.market && (mk === 'futures' || mk === 'spot')) {
          // Piața se schimbă pe loc (fără remontarea secțiunii vechi); lista se montează
          // mai jos, deja pe piața cerută.
          this.market = mk;
          settings.set('sx_market', mk);
          market.start(this.exchange, mk);
        }
      }
    }

    this.current?.unmount();
    clear(this.viewEl);
    this.current = new route.section(this);
    const gata = this.current.mount(this.viewEl);
    document.title = inScreener ? `${route.label} — ScalpScreener` : 'ScalpScreener';

    // Moneda cerută din căutarea globală, când secțiunea de atunci n-avea grafic.
    if (this.pendingSymbol) {
      const symbol = this.pendingSymbol;
      this.pendingSymbol = null;
      Promise.resolve(gata).then(() => this.current?.panel?.setSymbol?.(symbol));
    }
  }

  showLocked(label) {
    toast(`«${label}» — раздел платформы, в скринере он не нужен`, 'info');
  }

  // ------------------------------------------------------- bursă / piață

  async setExchange(exchange) {
    if (exchange === this.exchange) return;
    this.exchange = exchange;
    settings.set('sx_exchange', exchange);
    client.disconnectAll();
    await market.start(exchange, this.market);
    this.#ensureSymbolExists();
    this.#remount();
    this.#refreshStatus();
    toast(`Биржа: ${exchange}`, 'success');
  }

  async setMarket(marketKind) {
    if (marketKind === this.market) return;
    this.market = marketKind;
    settings.set('sx_market', marketKind);
    await market.start(this.exchange, marketKind);
    this.#ensureSymbolExists();
    this.#remount();
    this.#refreshStatus();
  }

  /**
   * Multe simboluri există doar pe futures (sau doar pe spot). După comutare,
   * dacă cel deschis nu se găsește pe piața nouă, trecem pe cel mai lichid —
   * altfel graficul ar încerca să încarce o pereche inexistentă și ar rămâne gol.
   */
  #ensureSymbolExists() {
    const current = settings.get('sx_coins_symbol', 'BTCUSDT');
    if (market.get(current)) return;
    const best = market.list().sort((a, b) => (b.quoteVolume || 0) - (a.quoteVolume || 0))[0];
    if (!best) return;
    settings.set('sx_coins_symbol', best.symbol);
    toast(`${current} нет на этом рынке — открыл ${best.symbol}`);
  }

  #remount() {
    const section = this.current?.constructor;
    if (!section) return;
    this.current.unmount();
    clear(this.viewEl);
    this.current = new section(this);
    this.current.mount(this.viewEl);
  }

  // ---------------------------------------------------------- notificări

  /** Ecranul de intrare, singurul lucru vizibil fără cont. */
  #poarta() {
    this.current?.unmount();
    this.current = null;
    if (!this.mobil) this.tabsBarEl.style.display = 'none';
    this.shellEl.classList.add('inchis');
    clear(this.viewEl);
    this.poartaEl = ecranIntrare();
    this.viewEl.append(this.poartaEl);
    document.title = 'ScalpScreener — вход';
  }

  #startNotifications() {
    const poll = async () => {
      try {
        const data = await (await fetch('/api/notifications?limit=50')).json();
        this.notifications = data.items || [];
        // Mesajele directe şi înştiinţările sociale intră în acelaşi contor: pentru om
        // e o singură întrebare — „am ceva nou?" —, nu trei locuri de verificat.
        let personale = 0;
        let personaleMesaje = 0;
        let personaleDespre = 0;
        this.social = [];
        if (session.authenticated) {
          try {
            const soc = await (await fetch('/api/social/notifications')).json();
            this.social = soc.items || [];
            // Cate mesaje necitite ai plus cate lucruri s-au intamplat cu ce ai scris.
            // Instiintarea de mesaj e una pe conversatie, dar in meniu vrem numarul de
            // mesaje — asa arata cifra cat ai de citit, nu cate ferestre sa deschizi.
            const pe = soc.pe_feluri || {};
            personaleMesaje = pe.mesaje || 0;
            personaleDespre = pe.despre_mine || 0;
            personale = personaleMesaje + personaleDespre;
          } catch { /* serverul poate lipsi; clopotelul ramane pe alertele de pret */ }
        }
        const unread = (data.unread || 0) + personale;
        this.bellCountEl?.classList.toggle('has-unread', unread > 0);
        if (this.bellCountEl) this.bellCountEl.textContent = String(unread);
        this.bellEl?.classList.toggle('has-unread', unread > 0);
        // Meniul arata doar ce e personal — mesaje si raspunsuri. Alertele de pret au
        // clopotelul lor in bara screenerului; amestecate, cifra de langa „Сообщения"
        // ar spune ca te asteapta cineva cand de fapt s-a miscat un pret.
        this.sidebar?.setBadge(personale);
        this.mobileShell?.setBadge(personale);

        const prefs = settings.get('sx_alert_prefs', {}) || {};
        const suna = prefs.sound_enabled !== false;
        // Trei feluri de „ceva nou", trei sunete: mesajul suna a doua note care urca
        // (cineva te-a strigat), instiintarea o singura nota scurta (ceva s-a intamplat
        // cu ce ai scris), alerta de pret ramane cu tonul ei dintotdeauna. Din alta
        // camera se aude care e care fara sa te uiti la ecran.
        const pe = this.ultimele || {};
        // Alerta de nivel (clopoțelul de pe grafic) are sunetul ei, MP3-ul ales; restul
        // creșterilor contorului (listări noi etc.) rămân pe tonul scurt — altfel fiecare
        // listare nouă ar suna ca o atingere de nivel.
        const alerteNivel = this.notifications.filter((n) => n.kind === 'price_alert');
        const ultimaAlerta = Math.max(0, ...alerteNivel.map((n) => n.id));
        // Rezerva: alertele pe care fluxul în timp real nu le-a adus (conexiune căzută). Cele
        // de dinainte de deschiderea paginii nu — ar umple ecranul cu ce s-a întâmplat ieri.
        const noi = this.ultimaAlerta === undefined ? []
          : alerteNivel.filter((n) => n.id > this.ultimaAlerta).sort((a, b) => a.id - b.id);
        this.ultimaAlerta = Math.max(this.ultimaAlerta ?? 0, ultimaAlerta);
        const aratate = this.#alerteNoi(noi);
        if (aratate) { /* sunetul l-a dat deja #alerteNoi */ }
        else if (suna && (pe.mesaje ?? personaleMesaje) < personaleMesaje) this.#sunet('mesaj');
        else if (suna && (pe.despre ?? personaleDespre) < personaleDespre) this.#sunet('instiintare');
        else if (suna && unread > (this.lastUnread ?? unread)) this.#sunet('notificare');
        this.ultimele = { mesaje: personaleMesaje, despre: personaleDespre };
        this.lastUnread = unread;
      } catch { /* serverul poate lipsi */ }
    };
    poll();
    setInterval(poll, 10_000);
    this.#alerteLive();
  }

  /**
   * Alertele de nivel în clipa declanșării: serverul le împinge pe `/api/alerts/events`
   * (server-sent events), deci bannerul și sunetul nu mai așteaptă întrebarea de la 10 s.
   * `EventSource` se reconectează singur dacă legătura cade; între timp rămâne rezerva.
   */
  #alerteLive() {
    if (this.fluxAlerte || !window.EventSource || !session.authenticated) return;
    const es = new EventSource('/api/alerts/events');
    es.addEventListener('alert', (e) => {
      let n;
      try { n = JSON.parse(e.data); } catch { return; }
      if (n.id) this.ultimaAlerta = Math.max(this.ultimaAlerta ?? 0, n.id);
      this.#alerteNoi([n]);
    });
    this.fluxAlerte = es;
  }

  /** Banner + sunet, o singură dată pe alertă (fluxul și rezerva pot aduce aceeași). */
  #alerteNoi(lista) {
    this.alerteVazute ||= new Set();
    const noi = lista.filter((n) => n && n.kind === 'price_alert' && !this.alerteVazute.has(n.id));
    if (!noi.length) return false;
    for (const n of noi) this.alerteVazute.add(n.id);
    this.banners ||= new AlertBanners((info) => this.openWatch(info));
    for (const n of noi) this.banners.show(n);
    const prefs = settings.get('sx_alert_prefs', {}) || {};
    if (prefs.sound_enabled !== false) this.#sunet('alerta');
    return true;
  }

  /**
   * Sunet scurt prin WebAudio — fara fisier audio de livrat.
   *
   * Trei glasuri, ca sa se deosebeasca fara sa te uiti: mesajul urca doua note, o
   * instiintare e o nota singura si mai inalta, alerta de pret pastreaza tonul vechi.
   * Volumul e mic pe scop; un sunet de aplicatie care sperie omul se opreste din setari
   * a doua zi si nu mai anunta niciodata nimic.
   */
  #sunet(fel = 'alerta') {
    // Alerta de preț (clopoțelul de pe niveluri) are sunetul ales de utilizator, un MP3.
    // Dacă browserul nu-l lasă să pornească (autoplay blocat până la primul clic pe
    // pagină), cade pe tonul generat de mai jos — o alertă nu trebuie să treacă în tăcere.
    if (fel === 'alerta') {
      try {
        const a = this.sunetAlerta || (this.sunetAlerta = new Audio('/static/sounds/alerta.mp3'));
        a.volume = 0.8;
        a.currentTime = 0;
        const p = a.play();
        if (p && p.catch) p.catch(() => this.#ton(fel));
        return;
      } catch { /* cade pe ton */ }
    }
    this.#ton(fel);
  }

  #ton(fel) {
    const note = {
      mesaj: [[660, 0], [880, 0.09]],
      instiintare: [[990, 0]],
      alerta: [[880, 0]],
      notificare: [[880, 0]],
    }[fel] || [[880, 0]];
    try {
      const ctx = this.audio || (this.audio = new (window.AudioContext || window.webkitAudioContext)());
      // Contextul poate ramane suspendat pana la prima atingere a paginii; il trezim
      // fara sa asteptam, ca sunetul urmator sa prinda.
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      for (const [frecventa, intarziere] of note) {
        const t = ctx.currentTime + intarziere;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = frecventa;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.12, t + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
        osc.start(t);
        osc.stop(t + 0.24);
      }
    } catch { /* politica de autoplay poate bloca; nu e un motiv sa cada nimic */ }
  }

  /**
   * Textul unei instiintari personale.
   *
   * Verdictul nu are actor: nu l-a dat un om, l-a dat pretul. De aceea e singurul rand
   * din lista care incepe cu ce s-a intamplat, nu cu cine a facut ceva.
   */
  #textSocial(n) {
    const cine = n.actor?.username || '';
    switch (n.kind) {
      case 'comment': return `${cine} ответил на вашу публикацию`;
      case 'mention': return `${cine} упомянул вас`;
      case 'reaction': return `${cine} отреагировал на вашу публикацию`;
      case 'follow': return `${cine} подписался на вас`;
      case 'verdict': return `Вердикт по вашей идее: ${n.text}`;
      case 'dm': return `${cine} написал вам лично`;
      default: return n.text || '';
    }
  }

  /** Pagina in care se rezolva tot ce e personal. */
  deschideMesaje(fila = 'chats') {
    document.querySelector('.notif-panel')?.remove();
    document.querySelector('.sh-fundal')?.remove();
    this.navigate(fila === 'mine' ? '/messages/?tab=alerts' : '/messages/', { fortat: true });
  }

  #deschideSocial(n) {
    const eu = session.user?.username;
    // Fiecare fel de instiintare duce acolo unde se rezolva: mesajul in conversatie,
    // abonatul pe profilul lui, restul la publicatiile tale.
    if (n.kind === 'dm' && n.actor?.username) {
      document.querySelector('.notif-panel')?.remove();
      document.querySelector('.sh-fundal')?.remove();
      import('./ui/messages.js').then(({ deschideMesaje }) => deschideMesaje(n.actor.username));
      return;
    }
    if (n.kind === 'follow' && n.actor?.username) {
      this.navigate(`/profile/${encodeURIComponent(n.actor.username)}/`);
      document.querySelector('.notif-panel')?.remove();
      document.querySelector('.sh-fundal')?.remove();
      return;
    }
    if (eu) this.navigate(`/feed/?author=${encodeURIComponent(eu)}`, { fortat: true });
  }

  /** Publica: o cheama si bara de jos de pe telefon. */
  openNotifications() { this.#openNotifications(); }

  #openNotifications() {
    const list = el('div', { class: 'notif-list' });
    const items = this.notifications || [];
    const social = this.social || [];

    // Intai cele personale: cineva ti-a scris sau piata a inchis socoteala cu ideea ta.
    // O alerta de pret poate astepta un rand mai jos; un raspuns adresat tie, nu.
    for (const n of social) {
      list.append(el('button', { class: `notif soc${n.read ? '' : ' unread'}`,
        onclick: () => this.#deschideSocial(n) }, [
        el('span', { class: 'notif-time',
          text: new Date(n.created_at * 1000).toLocaleString('ru-RU') }),
        el('span', { class: 'notif-text', text: this.#textSocial(n) }),
        n.post_text ? el('span', { class: 'notif-cit', text: n.post_text }) : null,
      ]));
    }

    if (!items.length && !social.length) {
      list.append(el('div', { class: 'notif-empty', text: 'Уведомлений пока нет' }));
    }
    for (const item of items) {
      list.append(el('div', { class: `notif${item.read ? '' : ' unread'}` }, [
        el('span', { class: 'notif-time', text: new Date(item.ts * 1000).toLocaleString('ru-RU') }),
        el('span', { class: 'notif-text', text: item.text }),
      ]));
    }

    const panel = el('div', { class: 'notif-panel' }, [
      el('div', { class: 'notif-head' }, [
        el('span', { text: 'Уведомления' }),
        el('button', {
          class: 'notif-clear', text: 'Очистить',
          onclick: async () => {
            await fetch('/api/notifications/clear', { method: 'POST' });
            panel.remove();
          },
        }),
        el('button', { class: 'notif-close', onclick: () => panel.remove() }, [icon('close', 13)]),
      ]),
      list,
    ]);

    document.querySelector('.notif-panel')?.remove();
    // Pe telefon, panoul din colt n-are unde sta: acelasi continut urca de jos.
    if (this.mobil) {
      import('./ui/sheet.js').then(({ foaie }) => foaie({
        titlu: 'Уведомления', corp: list, inalta: true,
      }));
    } else {
      document.body.append(panel);
    }
    Promise.all([
      fetch('/api/notifications/read', { method: 'POST' }),
      session.authenticated
        ? fetch('/api/social/notifications/read', { method: 'POST' }) : null,
    ].filter(Boolean)).then(() => {
      if (this.bellCountEl) this.bellCountEl.textContent = '0';
      this.bellEl?.classList.remove('has-unread');
      this.sidebar?.setBadge(0);
      this.mobileShell?.setBadge(0);
    });
  }
}

// Tema se aplică înainte de prima randare: altfel interfața ar apărea o clipă în
// paleta implicită și abia apoi ar sări în cea aleasă.
// Fara `persist`: pornirea doar aplica tema, n-o alege. Altfel fiecare vizitator ar
// pleca de pe site cu o tema „aleasa" pe care n-a cerut-o, si n-ar mai prinde niciodata
// o schimbare a temei implicite.
applyTheme(undefined, { persist: false });

const app = new App();
app.boot().catch((err) => {
  console.error('[boot]', err);
  document.body.append(el('div', { class: 'boot-error', text: `Ошибка запуска: ${err.message}` }));
});

window.app = app;
